import Bluebird from "bluebird";
import { Session } from "@/tools/msa/models";
import isLoading from "@/tools/msa/stores/loading";
import workingDocument from "@/tools/msa/stores/document";
import { Page } from "@/tools/msa/models/requests/extraction/legacy";
import { makePartialDocument } from "@/tools/msa/processing/document";
import { encodeDocumentWithSnomed } from "@/tools/msa/processing/snomed";
import { encodeDocumentWithCustomTerms } from "@/tools/msa/processing/custom";

const findDocumentIndexForPage = (session: Session, number: number) =>
  session.documents.findIndex(document =>
    document.pages.map(page => page.number).includes(number)
  );

const findDocumentForPage = (session: Session, number: number) =>
  session.documents[findDocumentIndexForPage(session, number)];

export const prependToNextDocument = async (session: Session, page: Page) => {
  isLoading.value = true;
  try {
    const currentIndex = findDocumentIndexForPage(session, page.number);
    const nextIndex = findDocumentIndexForPage(session, page.number + 1);
    if (
      nextIndex !== currentIndex + 1 &&
      !confirm(
        `This page will move to the start of the document #${nextIndex +
          1}. Do you wish to proceed?`
      )
    )
      return;

    const [current, next] = await makeDocuments(
      [
        session.documents[currentIndex].pages.slice(0, -1),
        [
          ...session.documents[currentIndex].pages.slice(-1),
          ...session.documents[nextIndex].pages
        ]
      ],
      session
    );

    const documents = [...session.documents];
    documents[currentIndex] = current;
    documents[nextIndex] = next;
    session.documents = documents.filter(document => document.pages.length > 0);
    workingDocument.value =
      findDocumentForPage(session, page.number - 1) ||
      findDocumentForPage(session, page.number);
  } finally {
    isLoading.value = false;
  }
};

export const appendToPreviousDocument = async (
  session: Session,
  page: Page
) => {
  isLoading.value = true;
  try {
    const currentIndex = findDocumentIndexForPage(session, page.number);
    const previousIndex = findDocumentIndexForPage(session, page.number - 1);
    if (
      previousIndex !== currentIndex - 1 &&
      !confirm(
        `This page will move to the end of the document #${previousIndex +
          1}. Do you wish to proceed?`
      )
    )
      return;

    const [current, previous] = await makeDocuments(
      [
        session.documents[currentIndex].pages.slice(1),
        [
          ...session.documents[previousIndex].pages,
          ...session.documents[currentIndex].pages.slice(0, 1)
        ]
      ],
      session
    );

    const documents = [...session.documents];
    documents[currentIndex] = current;
    documents[previousIndex] = previous;
    session.documents = documents.filter(document => document.pages.length > 0);
    workingDocument.value =
      findDocumentForPage(session, page.number + 1) ||
      findDocumentForPage(session, page.number);
  } finally {
    isLoading.value = false;
  }
};

export const splitDocument = async (session: Session, page: Page) => {
  isLoading.value = true;
  try {
    const documentIndex = findDocumentIndexForPage(session, page.number);
    if (documentIndex === -1) return;
    const document = session.documents[documentIndex];
    const pageIndex = document.pages.findIndex(
      current => current.number === page.number
    );
    if (pageIndex === -1) return;

    const documents = [...session.documents];
    documents.splice(
      documentIndex,
      1,
      ...(await makeDocuments(
        [
          document.pages.slice(0, pageIndex + 1),
          document.pages.slice(pageIndex + 1)
        ],
        session
      ))
    );
    session.documents = documents;
    workingDocument.value = findDocumentForPage(session, page.number);
  } finally {
    isLoading.value = false;
  }
};

const makeDocuments = async (pagesByDocument: Page[][], session: Session) =>
  await Bluebird.resolve(pagesByDocument)
    .map(pages => makePartialDocument(pages, session), { concurrency: 6 })
    .map(document => encodeDocumentWithSnomed(document, session), {
      concurrency: 6
    })
    .map(document => encodeDocumentWithCustomTerms(document, session), {
      concurrency: 6
    });
